import React, { useState, useEffect } from 'react';
import { useTheme, getT, getBrandColors} from '../ThemeContext';
import PageHeaderIcon from "../components/PageHeaderIcon";
import API_BASE from '../apiConfig';
import { FaTrophy } from 'react-icons/fa';

import TournamentCostPlanner from './TournamentCostPlanner';
import TournamentScheduler from './TournamentScheduler';
import TournamentExpense from './TournamentExpense';
import TournamentPLReport from './TournamentPLReport';
import TournamentAdminApproval from './TournamentAdminApproval';

const BASE = API_BASE;
const FONT = "'Segoe UI', system-ui, sans-serif";
const BLUR = "blur(18px)";

const TABS = [
  { key: 'planner',   label: 'Cost Planner' },
  { key: 'scheduler', label: 'Scheduler' },
  { key: 'expense',   label: 'Expense' },
  { key: 'pnl',       label: 'P&L Report' },
  { key: 'admin',     label: 'Admin Hub' },
];

// Tabs whose content is driven by the shared top filter (a tournament must be picked).
const FILTER_DRIVEN = new Set(['scheduler', 'expense', 'pnl', 'admin']);

const TournamentHub = () => {
  const { isDark } = useTheme();
  const T = getT(isDark);

  const [tab, setTab] = useState('planner');
  const [tournaments, setTournaments] = useState([]);
  const [tournamentId, setTournamentId] = useState('');
  const [tournament, setTournament] = useState(null);        // full detail (for categories)
  const [ageCategoryId, setAgeCategoryId] = useState('');
  const [ageCode, setAgeCode] = useState('');

  useEffect(() => { loadTournaments(); }, []);

  const loadTournaments = async () => {
    try {
      const res = await fetch(`${BASE}/tournament`);
      const data = await res.json();
      if (data.success) setTournaments(data.tournaments || []);
    } catch (err) { console.error(err); }
  };

  const selectTournament = async (id) => {
    setTournamentId(id);
    setAgeCategoryId(''); setAgeCode('');
    if (!id) { setTournament(null); return; }
    try {
      const res = await fetch(`${BASE}/tournament/${id}`);
      const data = await res.json();
      if (data.success) setTournament(data);
    } catch (err) { console.error(err); }
  };

  const onCategoryChange = (id) => {
    setAgeCategoryId(id);
    const cat = (tournament?.age_categories || []).find(c => String(c.age_category_id) === String(id));
    setAgeCode(cat?.age_code || '');
  };

  // When the Cost Planner tab hands off after creating a tournament, adopt that
  // tournament into the shared filter and jump to the Scheduler tab.
  const handlePlannerNavigate = () => {
    const pid = sessionStorage.getItem('scheduler_prefill_tournament_id');
    if (pid) {
      sessionStorage.removeItem('scheduler_prefill_tournament_id'); // avoid double-load in embedded Scheduler
      loadTournaments();
      selectTournament(pid);
    }
    setTab('scheduler');
  };

  const glassCard = {
    background: T.glass, backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
    border: T.glassBdr, borderRadius: '14px', boxShadow: T.glassSh,
    padding: '16px 20px', boxSizing: 'border-box', marginBottom: '14px',
  };
  const labelStyle = {
    display: 'block', marginBottom: '6px', fontSize: '10.5px', fontWeight: 700,
    letterSpacing: '0.06em', textTransform: 'uppercase', color: T.textLabel, fontFamily: FONT,
  };
  const inputStyle = {
    border: `1.5px solid ${T.inputBdr}`, borderRadius: '8px', padding: '9px 12px',
    background: T.inputBg, backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
    width: '100%', boxSizing: 'border-box', outline: 'none', fontSize: '13px',
    color: T.inputText, fontFamily: FONT,
  };
  const tabBtn = (active) => ({
    padding: '9px 16px', borderRadius: '9px', border: 'none', cursor: 'pointer',
    fontSize: '13px', fontWeight: 700, fontFamily: FONT,
    background: active ? getBrandColors().yellow : T.glass,
    color: active ? getBrandColors().blueDark : T.textLabel,
    boxShadow: active ? '0 4px 14px rgba(247,206,11,0.35)' : 'none',
    border: active ? 'none' : `1px solid ${T.inputBdr}`,
  });

  return (
    <div style={{ minHeight: '100dvh', padding: '18px 26px 40px', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <PageHeaderIcon icon={<FaTrophy size={19} color={getBrandColors().blueDark} />} />
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 800, color: T.textPrimary, margin: 0, fontFamily: FONT }}>Tournament</h1>
          <p style={{ fontSize: '12px', color: T.textMuted, margin: '2px 0 0', fontFamily: FONT }}>Plan, schedule, book expenses, review P&amp;L, and approve payments — all in one place</p>
        </div>
      </div>

      {/* Shared top filter */}
      <div style={glassCard}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '14px' }}>
          <div>
            <label style={labelStyle}>Tournament</label>
            <select value={tournamentId} onChange={(e) => selectTournament(e.target.value)} style={inputStyle}>
              <option value="">— Choose —</option>
              {tournaments.map(t => (
                <option key={t.tournament_id} value={t.tournament_id}>{t.tournament_name} ({t.status})</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Age Category</label>
            <select value={ageCategoryId} onChange={(e) => onCategoryChange(e.target.value)} style={inputStyle} disabled={!tournament}>
              <option value="">All</option>
              {(tournament?.age_categories || []).map(c => (
                <option key={c.age_category_id} value={c.age_category_id}>{c.category_name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={tabBtn(tab === t.key)}>{t.label}</button>
        ))}
      </div>

      {/* Prompt for the filter-driven tabs when nothing is selected */}
      {FILTER_DRIVEN.has(tab) && !tournamentId && (
        <div style={{ ...glassCard, textAlign: 'center', color: T.textMuted, fontSize: '13px', padding: '28px' }}>
          Pick a tournament in the filter above to use the {TABS.find(t => t.key === tab)?.label} tab.
        </div>
      )}

      {/* Active tab content */}
      {tab === 'planner' && <TournamentCostPlanner onNavigate={handlePlannerNavigate} />}

      {tab === 'scheduler' && tournamentId && (
        <TournamentScheduler embedded extTournamentId={tournamentId} extAgeCategoryId={ageCategoryId} />
      )}
      {tab === 'expense' && tournamentId && (
        <TournamentExpense embedded extTournamentId={tournamentId} extAgeCategoryId={ageCategoryId} />
      )}
      {tab === 'pnl' && tournamentId && (
        <TournamentPLReport embedded extTournamentId={tournamentId} extAgeCode={ageCode} />
      )}
      {tab === 'admin' && tournamentId && (
        <TournamentAdminApproval embedded extTournamentId={tournamentId} extAgeCategoryId={ageCategoryId} />
      )}
    </div>
  );
};

export default TournamentHub;
